/**
 * Document rows pre-filled from what is already on file, and their upload - the WO create rules, shared by the work
 * order detail page's Upload document dialog and Add customer.
 *
 * - An employee document type is pre-filled from the applicant's Thaklis employee (its current version, whatever its
 *   expiry); a company type (holder ORGANIZATION) from the customer organization's current version.
 * - A kept pre-filled employee file is uploaded to the work order only (it came from Thaklis); a kept pre-filled
 *   company file is attached by its S3 key (no second upload). A removed one goes nowhere.
 * - Every file is put on the work order with the applicant it belongs to (name and employee uid), company documents
 *   too. A new file is uploaded with its type code: a client's company document is stored in its documents first, an
 *   employee document is then synced to the employee by the caller.
 */

import ApiService from '../../services/api.service';
import { tcOrgDocumentApi } from '../org-documents/orgDocumentApi';
import { isOrganizationDocType } from '../../utils/docTypeGroups';

/** The applicant's Thaklis documents (groups), or [] when unknown / not reachable. */
export const loadEmployeeDocGroups = async (customerKey, uid) => {
  if (!customerKey || !uid) return [];
  try {
    const res = await ApiService.getCustomerEmployeeDocuments(customerKey, uid);
    const data = res?.data?.data;
    return Array.isArray(data) ? data : (data?.groups || data?.documentGroups || []);
  } catch {
    return [];
  }
};

/** The organization's company documents (groups), or [] when not a client / no access. */
export const loadOrgDocGroups = async (orgId) => {
  if (!orgId) return [];
  try {
    const { groups } = await tcOrgDocumentApi(orgId).documents();
    return groups || [];
  } catch {
    return [];
  }
};

/** The current version of a type among groups ({ common: { documentType, isCurrent }, documents }). */
export const currentGroupOf = (groups, code) => {
  const ofType = (groups || []).filter(g => (g.common?.documentType || '').toUpperCase() === (code || '').toUpperCase());
  return ofType.find(g => g.common?.isCurrent) || ofType[0] || null;
};

/**
 * Fetch the file of a pre-fill and stage it as a document row's file. kind 'employee' is marked _fromEmployee,
 * kind 'org' carries _orgDocKey (the S3 key the work order reuses). Resolves to null when there is no file.
 */
export const fetchPrefilledDoc = async ({ kind, group, code, label, customerKey, orgId }) => {
  const meta = group?.documents?.[0];
  if (!meta?.filePath) return null;
  const res = kind === 'org'
    ? await tcOrgDocumentApi(orgId).view(meta.filePath)
    : await ApiService.getCustomerEmployeeFile(customerKey, meta.filePath);
  const blob = res.data;
  const name = meta.fileName || 'document';
  const file = new File([blob], name, { type: blob?.type || meta.contentType || 'application/octet-stream' });
  return {
    id: Date.now() + Math.random(),
    file,
    fileName: name,
    fileSize: file.size,
    documentType: code,
    documentTypeName: label || code,
    extracting: false,
    documentNumber: group.common?.documentNumber || null,
    issueDate: group.common?.issueDate || null,
    expiryDate: group.common?.expiryDate || null,
    ...(kind === 'org' ? { _orgDocKey: meta.filePath } : { _fromEmployee: true }),
  };
};

/** Pre-fill kind of a type: company documents from the organization, the rest from the employee. */
export const prefillKindOf = (documentTypes, code) => (isOrganizationDocType(documentTypes, code) ? 'org' : 'employee');

/** A staged file that must still be sent to the applicant's Thaklis employee (new, employee type, uploaded). */
export const needsEmployeeSync = (documentTypes, d) =>
  !!d.file && !d._fromEmployee && !d._orgDocKey && !isOrganizationDocType(documentTypes, d.documentType);

/**
 * Put the staged documents on the work order. Returns the ids of the ones that failed (never synced to Thaklis).
 */
export const submitWorkOrderDocuments = async ({
  workOrderId, docs, customerName = null, employeeUid = null, documentTypes = [], description = '',
}) => {
  const failedIds = new Set();
  for (const d of (docs || [])) {
    if (!d.file) continue;
    try {
      if (d._orgDocKey) {
        await ApiService.attachWorkOrderOrganizationDocument(workOrderId, {
          s3Key: d._orgDocKey, documentType: d.documentTypeName || d.documentType, customerName, employeeUid,
        });
      } else {
        await ApiService.uploadWorkOrderDocument(workOrderId, d.file, description, d.documentTypeName || d.documentType,
          customerName, null, null, employeeUid, d.documentType || null);
      }
    } catch (err) {
      console.error('Error putting the document on the work order:', err);
      failedIds.add(d.id);
    }
  }
  return failedIds;
};
